import { httpClient } from "./client";
import type { TransactionApi } from "./interfaces";
import type {
  ChargeRequest,
  TransactionResponse,
  PlayerTransactionReceiptResponse,
  StripeFrontendConfigResponse,
  SavedPaymentMethodResponse,
  PaymentMethodSetupIntentResponse,
  UUID,
} from "../types";

// NOTE: the membership subscription / visit-balance methods were REMOVED from
// this client. They were unused and could not bill (subscribe() sent no payment
// method → transaction_id was always None). The membership journey now owns a
// single authoritative client, `membershipApi` (./membership.ts), whose
// subscribe() carries the card. See .claude/rules/clean-migration.md (no dual-path).
export const transactionApi: TransactionApi = {
  charge: (data: ChargeRequest) =>
    httpClient.post<TransactionResponse>("/transactions/charge", data),

  refund: (transactionId: UUID) =>
    httpClient.post<TransactionResponse>(
      `/transactions/refund/${transactionId}`,
    ),

  getHistory: (params) =>
    httpClient.get<PlayerTransactionReceiptResponse[]>(
      `/transactions/history?limit=${params?.limit ?? 50}&offset=${params?.offset ?? 0}`,
    ),

  getDetail: (transactionId: UUID) =>
    httpClient.get<PlayerTransactionReceiptResponse>(
      `/transactions/${encodeURIComponent(transactionId)}`,
    ),

  // --- Facility-scoped Stripe Connect payment methods (player self-service) ---
  // FacilityId remains the routing/authorization key, while facilities in the
  // same organization may resolve to the same connected account. The response
  // carries connected_account_id so client safety state follows the real wallet
  // across those aliases. SetupIntent confirmation still must use that account.
  getFrontendConfig: (facilityId: UUID) =>
    httpClient.get<StripeFrontendConfigResponse>(
      `/transactions/facilities/${facilityId}/frontend-config`,
    ),

  getWalletFrontendConfig: () =>
    httpClient.get<StripeFrontendConfigResponse>(
      "/users/me/payment-methods/frontend-config",
    ),

  listPaymentMethods: (facilityId: UUID) =>
    httpClient.get<SavedPaymentMethodResponse[]>(
      `/transactions/facilities/${facilityId}/payment-methods`,
    ),

  createSetupIntent: (facilityId: UUID, idempotencyKey: UUID) =>
    httpClient.post<PaymentMethodSetupIntentResponse>(
      `/transactions/facilities/${facilityId}/payment-methods/setup-intent`,
      { idempotency_key: idempotencyKey },
    ),

  createWalletSetupIntent: (idempotencyKey: UUID) =>
    httpClient.post<PaymentMethodSetupIntentResponse>(
      "/users/me/payment-methods/setup-intent",
      { idempotency_key: idempotencyKey },
    ),

  removePaymentMethod: (facilityId: UUID, paymentMethodId: string) =>
    httpClient.delete<void>(
      `/transactions/facilities/${facilityId}/payment-methods/${encodeURIComponent(
        paymentMethodId,
      )}`,
    ),

  // --- Player-scoped cross-org saved cards (#1392) ---
  // Aggregates the caller's saved cards across EVERY org's connected account.
  // SERVER-SCOPED to the auth user (no uid is ever passed). Each row carries a
  // composite `id = "{connect_account_id}:{payment_method_id}"` (the delete key)
  // plus the owning `org_id`/`org_name`. Calls the canonical `/users/me/...`
  // path directly (httpClient prefixes `/bff/api`, so the BFF sees
  // `/bff/api/users/me/...`) — NOT the bare `/me/...`, so it does not rely on
  // the BFF's `/me/ → /users/me/` rewrite quirk.
  listMyPaymentMethods: () =>
    httpClient.get<SavedPaymentMethodResponse[]>("/users/me/payment-methods"),

  removeMyPaymentMethod: (compositeId: string) =>
    httpClient.delete<void>(
      `/users/me/payment-methods/${encodeURIComponent(compositeId)}`,
    ),
};
